import { Card, Typography } from 'antd';
import { FC } from 'react';

const { Text } = Typography;

interface LegendStop {
  color: string;
  label: string;
}

interface LegendConfig {
  unit: string;
  title: string;
  stops: LegendStop[];
}

type LegendDataType = {
  precipitation_new: LegendConfig;
  temp_new: LegendConfig;
  wind_new: LegendConfig;
};

const legendData: LegendDataType = {
  precipitation_new: {
    unit: 'mm',
    title: 'Precipitation',
    stops: [
      { color: 'rgba(255, 255, 255, 0)', label: 'None' },
      { color: 'rgb(170, 170, 215)', label: 'Very Light' },
      { color: 'rgb(110, 110, 175)', label: 'Light' },
      { color: 'rgb(37, 37, 136)', label: 'Moderate' },
      { color: 'rgb(12, 12, 80)', label: 'Heavy' },
    ],
  },
  temp_new: {
    unit: '°C',
    title: 'Temperature',
    stops: [
      { color: 'rgba(130, 22, 146, 1)', label: '< -30' },
      { color: 'rgba(32, 140, 236, 1)', label: '-20' },
      { color: 'rgba(35, 221, 221, 1)', label: '0' },
      { color: 'rgba(194, 255, 40, 1)', label: '10' },
      { color: 'rgba(255, 240, 40, 1)', label: '20' },
      { color: 'rgba(252, 128, 20, 1)', label: '30+' },
    ],
  },
  wind_new: {
    unit: 'm/s',
    title: 'Wind Speed',
    stops: [
      { color: 'rgba(255,255,255, 0)', label: '< 1 (Calm)' },
      { color: 'rgba(238,206,206, 0.4)', label: '5 (Light)' },
      { color: 'rgba(179,100,188, 0.7)', label: '15 (Moderate)' },
      { color: 'rgba(63,33,59, 0.8)', label: '25 (Strong)' },
      { color: 'rgba(70,0,175,1)', label: '> 50 (Very Strong)' },
    ],
  },
};

interface WeatherLegendProps {
  activeLayerType?: string;
  weatherLayerOptions: Record<string, string>;
}

const WeatherLegend: FC<WeatherLegendProps> = ({
  activeLayerType,
  weatherLayerOptions,
}) => {
  if (!activeLayerType || !(activeLayerType in legendData)) {
    return null;
  }

  const currentLegend = legendData[activeLayerType as keyof LegendDataType];
  const layerDisplayName =
    weatherLayerOptions[activeLayerType] || currentLegend.title;

  return (
    <Card
      title={`${layerDisplayName} (${currentLegend.unit})`}
      size='small'
      style={{
        backgroundColor: 'var(--surface-elevated)',
        maxWidth: '200px',
        maxHeight: '300px',
        overflowY: 'auto',
      }}
      bodyStyle={{ padding: '8px' }}
    >
      {currentLegend.stops.map((stop, index) => (
        <div
          key={index}
          style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}
        >
          <div
            style={{
              width: '20px',
              height: '20px',
              backgroundColor: stop.color,
              marginRight: '8px',
              border: '1px solid var(--gray-300)',
            }}
          />
          <Text style={{ fontSize: '12px', color: 'var(--text-primary)' }}>
            {stop.label}
          </Text>
        </div>
      ))}
    </Card>
  );
};

export default WeatherLegend;
